import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { motion } from 'framer-motion';
import posthog from 'posthog-js';
import { useNavigate, useLocation } from 'react-router-dom';
import axios from 'axios';
import API_URL from '../config';
import { useTour } from '../contexts/TourContext';
import ProductTour from './Tour/ProductTour';
import { dashboardTour } from '../tours/tourConfigs';
import ComplianceTimelineWidget from './Dashboard/ComplianceTimelineWidget';
import DriScoreHero from './Dashboard/DriScoreHero';
import { Button } from './ui';
import { BrandLoading } from './Brand';
import {
  RefreshCw,
  X,
  FileText,
  Truck,
  Shield,
  AlertTriangle,
  FileCheck,
  ShieldCheck,
  CreditCard,
  Users,
} from 'lucide-react';
import { formatAnalyticsDocumentDisplay } from '../utils/analyticsDocumentLabel';
import { shouldShowUnverifiedDocsBanner } from '../utils/documentVision';
import { readUserFromStorage, isFreeSubscription, mergeSubscriptionTier, getSubscriptionTier, notifySubscriptionUpdated } from '../utils/subscription';
import IdentityVerificationBanner from './Identity/IdentityVerificationBanner';
import IdentityStatusBadge from './Identity/IdentityStatusBadge';
import '../App.css';

function vehicleIssueFromAudit(missingItems) {
  const list = missingItems || [];
  const hit = list.find(
    (m) =>
      /vehicle|inspection|annual|dvir|maintenance/i.test(m.item || '') ||
      /vehicle/i.test(m.category || ''),
  );
  if (!hit) return null;
  const name = hit.detail ? `${hit.item} — ${hit.detail}` : hit.item;
  return name;
}

function authorityPillarStatus(compliance) {
  if (!compliance?.authority) {
    return { headline: 'Inactive', dotLine: 'No DOT on file' };
  }
  const auth = compliance.authority;
  const dot = auth.dotNumber ? `DOT ${auth.dotNumber}` : 'DOT on file';
  const op = auth.operatingStatus ? `${auth.operatingStatus.replace(/_/g, ' ')}` : null;
  const dotLine = op ? `${dot} · ${op}` : dot;

  if (compliance.overallStatus === 'non-compliant') {
    return { headline: 'Needs Focus', dotLine };
  }
  if (compliance.mcs150?.status === 'overdue' || compliance.ucr?.isExpired) {
    return { headline: 'Needs Focus', dotLine };
  }
  if (compliance.mcs150?.status === 'due-soon' || compliance.ucr?.status === 'expiring-soon') {
    return { headline: 'Needs Focus', dotLine };
  }
  return { headline: 'Current', dotLine };
}

function firstMissingDocLabel(missingItems) {
  const m = (missingItems || [])[0];
  if (!m) return null;
  return m.detail ? `${m.item} — ${m.detail}` : m.item;
}

function driPillStyle(score) {
  if (score == null || Number.isNaN(Number(score))) {
    return { label: '—', color: '#6b7280' };
  }
  const n = Number(score);
  if (n < 60) return { label: 'Needs Setup', color: '#DC2626' };
  if (n < 80) return { label: 'Needs Attention', color: '#E09B1A' };
  return { label: 'Compliance Verified', color: '#16A34A' };
}

/** Parse YYYY-MM-DD in local calendar (avoids UTC midnight shifting the day). */
function parseTimelineDateOnly(isoDate) {
  if (!isoDate) return null;
  const s = String(isoDate).slice(0, 10);
  const parts = s.split('-');
  if (parts.length !== 3) return new Date(isoDate);
  const y = parseInt(parts[0], 10);
  const m = parseInt(parts[1], 10) - 1;
  const d = parseInt(parts[2], 10);
  if (Number.isNaN(y) || Number.isNaN(m) || Number.isNaN(d)) return new Date(isoDate);
  return new Date(y, m, d);
}

function isComplianceDeadlineTimelineEvent(ev) {
  if (!ev?.date) return false;
  const t = ev.type || '';
  if (t === 'medical_card') {
    return !/\brequired\b/i.test(ev.title || '');
  }
  return (
    t === 'insurance' ||
    t === 'vehicle_registration' ||
    t === 'vehicle_inspection' ||
    t === 'ifta_quarter' ||
    t === 'mcs150' ||
    t === 'ucr' ||
    t === 'clearinghouse_consent'
  );
}

function isActionOnlyTimelineEvent(ev) {
  if (!ev) return false;
  if (ev.type === 'medical_card' && /\brequired\b/i.test(ev.title || '')) return true;
  return false;
}

const Dashboard = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { isTourComplete, markTourComplete } = useTour();
  const [showTour, setShowTour] = useState(false);
  const [showChatbotHint, setShowChatbotHint] = useState(false);
  const [user, setUser] = useState(null);
  const [showSuccessMessage, setShowSuccessMessage] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState('');
  const [timelineEvents, setTimelineEvents] = useState([]);
  const [auditData, setAuditData] = useState(null);
  const [scoresData, setScoresData] = useState(null);
  const [authorityData, setAuthorityData] = useState(null);
  const [driScore, setDriScore] = useState(null);
  const [fleetDriMetrics, setFleetDriMetrics] = useState(null);
  const [dashLoading, setDashLoading] = useState(true);
  const [driversList, setDriversList] = useState(null);
  const [driversLoading, setDriversLoading] = useState(false);
  const [passportMeta, setPassportMeta] = useState(null);
  const [passportBusy, setPassportBusy] = useState(false);
  const [passportCopyLabel, setPassportCopyLabel] = useState('Copy link');
  const [userDocuments, setUserDocuments] = useState([]);
  const [, setSubscriptionEpoch] = useState(0);
  const dashboardViewCapturedRef = useRef(false);
  const bumpSubscriptionEpoch = useCallback(() => {
    setSubscriptionEpoch((e) => e + 1);
  }, []);

  const token = localStorage.getItem('token');
  const auth = useMemo(() => (token ? { headers: { Authorization: `Bearer ${token}` } } : null), [token]);

  useEffect(() => {
    window.addEventListener('focus', bumpSubscriptionEpoch);
    window.addEventListener('storage', bumpSubscriptionEpoch);
    return () => {
      window.removeEventListener('focus', bumpSubscriptionEpoch);
      window.removeEventListener('storage', bumpSubscriptionEpoch);
    };
  }, [bumpSubscriptionEpoch]);

  useEffect(() => {
    if (!auth) return;
    const tier = getSubscriptionTier(user ?? readUserFromStorage());
    const useFleetScore = tier === 'pro' || tier === 'fleet';

    setDashLoading(true);
    const driRequest = useFleetScore ?
      axios.get(`${API_URL}/api/dri/fleet-score`, auth).then((r) => r.data).catch(() => null)
    : axios.get(`${API_URL}/api/dri/score`, auth).then((r) => r.data).catch(() => null);

    Promise.all([
      axios.get(`${API_URL}/api/compliance/timeline`, { params: { range: 90 }, ...auth }).then((r) => r.data?.events || []).catch(() => []),
      axios.get(`${API_URL}/api/audit/readiness`, auth).then((r) => r.data).catch(() => null),
      axios.get(`${API_URL}/api/compliance/scores`, auth).then((r) => r.data).catch(() => null),
      axios.get(`${API_URL}/api/authority/compliance`, auth).then((r) => r.data).catch(() => null),
      driRequest,
    ])
      .then(([events, audit, scores, authorityCompliance, driData]) => {
        setTimelineEvents(events);
        setAuditData(audit);
        setScoresData(scores);
        setAuthorityData(authorityCompliance ?? null);
        if (useFleetScore) {
          if (driData && typeof driData === 'object') {
            const s = driData.totalScore ?? driData.score;
            setDriScore(typeof s === 'number' ? s : null);
            setFleetDriMetrics(driData.fleetMetrics ?? null);
          } else {
            setDriScore(null);
            setFleetDriMetrics(null);
          }
        } else {
          const s = driData?.totalScore;
          setDriScore(typeof s === 'number' ? s : null);
          setFleetDriMetrics(null);
        }
      })
      .finally(() => setDashLoading(false));
  }, [auth, user]);

  useEffect(() => {
    if (!auth) {
      setUserDocuments([]);
      return;
    }
    axios
      .get(`${API_URL}/api/documents`, auth)
      .then((r) => setUserDocuments(Array.isArray(r.data) ? r.data : []))
      .catch(() => setUserDocuments([]));
  }, [auth, user]);

  useEffect(() => {
    if (!auth) {
      setDriversList(null);
      setDriversLoading(false);
      return;
    }
    const tier = getSubscriptionTier(user ?? readUserFromStorage());
    if (tier !== 'pro' && tier !== 'fleet') {
      setDriversList(null);
      setDriversLoading(false);
      return;
    }
    setDriversLoading(true);
    axios
      .get(`${API_URL}/api/drivers`, auth)
      .then((r) => (Array.isArray(r.data) ? r.data : []))
      .catch(() => [])
      .then(setDriversList)
      .finally(() => setDriversLoading(false));
  }, [auth, user]);

  useEffect(() => {
    if (!auth) return;
    axios
      .get(`${API_URL}/api/passport/me`, auth)
      .then((r) => setPassportMeta(r.data?.passport ?? null))
      .catch(() => setPassportMeta(null));
  }, [auth]);

  const refreshPassportMeta = () => {
    if (!auth) return;
    axios
      .get(`${API_URL}/api/passport/me`, auth)
      .then((r) => setPassportMeta(r.data?.passport ?? null))
      .catch(() => setPassportMeta(null));
  };

  const handleGeneratePassport = async () => {
    if (!auth) return;
    setPassportBusy(true);
    try {
      await axios.post(`${API_URL}/api/passport/generate`, {}, auth);
      await refreshPassportMeta();
      posthog.capture('compliance_passport_generated');
    } catch (e) {
      console.error(e);
    } finally {
      setPassportBusy(false);
    }
  };

  const handleRegeneratePassport = async () => {
    if (!auth) return;
    setPassportBusy(true);
    try {
      await axios.delete(`${API_URL}/api/passport/deactivate`, auth);
      await axios.post(`${API_URL}/api/passport/generate`, {}, auth);
      await refreshPassportMeta();
      posthog.capture('compliance_passport_generated');
    } catch (e) {
      console.error(e);
    } finally {
      setPassportBusy(false);
    }
  };

  const passportShareUrl =
    passportMeta && !passportMeta.isExpired && passportMeta.passportId ?
      `${window.location.origin}/passport/${passportMeta.passportId}`
    : null;

  const copyPassportLink = async () => {
    if (!passportShareUrl) return;
    try {
      await navigator.clipboard.writeText(passportShareUrl);
      setPassportCopyLabel('Copied!');
      setTimeout(() => setPassportCopyLabel('Copy link'), 2000);
    } catch (e) {
      console.error(e);
    }
  };

  useEffect(() => {
    if (!isTourComplete('dashboard')) {
      setTimeout(() => setShowTour(true), 1000);
    }
  }, [isTourComplete]);

  const handleTourFinish = () => {
    setShowTour(false);
    markTourComplete('dashboard');
    setTimeout(() => setShowChatbotHint(true), 500);
  };

  const dismissChatbotHint = useCallback(() => setShowChatbotHint(false), []);

  useEffect(() => {
    if (!showChatbotHint) return undefined;
    const onKeyDown = (event) => {
      if (event.key === 'Escape') dismissChatbotHint();
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [showChatbotHint, dismissChatbotHint]);

  useEffect(() => {
    const t = localStorage.getItem('token');
    const userData = localStorage.getItem('user');
    if (!t || !userData) {
      navigate('/login');
      return;
    }
    const fetchProfile = async () => {
      try {
        setIsLoading(true);
        setError('');
        const response = await axios.get(`${API_URL}/api/auth/profile`, {
          headers: { Authorization: `Bearer ${t}` },
        });
        if (response.data) {
          const withTier = mergeSubscriptionTier(response.data);
          setUser(withTier);
          localStorage.setItem('user', JSON.stringify(withTier));
          notifySubscriptionUpdated();
          if (withTier.onboardingCompleted !== true) {
            navigate('/onboarding');
            return;
          }
        }
      } catch (err) {
        if (err.response?.status === 401) {
          localStorage.removeItem('token');
          localStorage.removeItem('user');
          navigate('/login');
        } else {
          setError('Failed to load profile.');
          try {
            setUser(mergeSubscriptionTier(JSON.parse(userData)));
          } catch {
            navigate('/login');
          }
        }
      } finally {
        setIsLoading(false);
      }
    };
    fetchProfile();
    const urlParams = new URLSearchParams(location.search);
    if (urlParams.get('session_id')) {
      setShowSuccessMessage(true);
      window.history.replaceState({}, document.title, '/dashboard');
      const refreshProfileAfterCheckout = (delayMs) => {
        setTimeout(async () => {
          try {
            const response = await axios.get(`${API_URL}/api/auth/profile`, {
              headers: { Authorization: `Bearer ${t}` },
            });
            if (response.data) {
              const withTier = mergeSubscriptionTier(response.data);
              setUser(withTier);
              localStorage.setItem('user', JSON.stringify(withTier));
              notifySubscriptionUpdated();
              bumpSubscriptionEpoch();
            }
          } catch {
            /* ignore */
          }
        }, delayMs);
      };
      refreshProfileAfterCheckout(1500);
      refreshProfileAfterCheckout(4000);
    }
  }, [navigate, location, bumpSubscriptionEpoch]);

  useEffect(() => {
    if (isLoading || !user || dashLoading || dashboardViewCapturedRef.current) return;
    dashboardViewCapturedRef.current = true;
    posthog.capture('dashboard_viewed', {
      driScore: driScore,
      plan: user.subscriptionTier || user.subscription?.tier || 'free',
    });
  }, [isLoading, user, dashLoading, driScore]);

  const userForUpgradeBanner = useMemo(() => user ?? readUserFromStorage(), [user]);
  const showUpgradeBanner = Boolean(userForUpgradeBanner) && isFreeSubscription(userForUpgradeBanner);

  const isProOrFleetDashboard = useMemo(() => {
    const t = getSubscriptionTier(user ?? readUserFromStorage());
    return t === 'pro' || t === 'fleet';
  }, [user]);

  const driverComplianceSummary = useMemo(() => {
    if (!isProOrFleetDashboard || !Array.isArray(driversList)) return null;
    const total = driversList.length;
    const pass = driversList.filter((d) => d.status === 'compliant').length;
    const failing = total - pass;
    const pct = total === 0 ? 0 : Math.round((pass / total) * 100);
    return { total, pass, failing, pct };
  }, [isProOrFleetDashboard, driversList]);

  const attentionCount = useMemo(() => {
    const missing = auditData?.missingItems?.length || 0;
    const dueSoon = auditData?.dueSoonItems?.length || 0;
    return missing + dueSoon;
  }, [auditData]);

  const actionItems = useMemo(() => {
    const rows = [];
    let k = 0;
    const violationOrInspectionFix = (title, detail, fixUrl) => {
      const u = fixUrl || '/audit';
      if (u === '/violations') return '/counterparty';
      const blob = `${title || ''} ${detail || ''}`.toLowerCase();
      if (
        /\bviolation\b|\bviolations\b|\binspection violation\b|\brs\s*violation\b|\bfmcsa inspection\b|\bdot inspection\b|\binspection\s+record\b/.test(
          blob,
        )
      ) {
        return '/counterparty';
      }
      return u;
    };
    const add = (title, detail, fixUrl) => {
      rows.push({
        id: `ai-${k++}`,
        title,
        detail,
        fixUrl: violationOrInspectionFix(title, detail, fixUrl || '/audit'),
      });
    };
    for (const m of auditData?.missingItems || []) {
      add(m.item, m.detail, '/audit');
    }
    for (const ev of timelineEvents || []) {
      if (ev.severity !== 'high' && ev.severity !== 'med') continue;
      if (isComplianceDeadlineTimelineEvent(ev)) {
        const eventDay = parseTimelineDateOnly(ev.date);
        if (!eventDay) continue;
        eventDay.setHours(0, 0, 0, 0);
        const today = new Date();
        today.setHours(0, 0, 0, 0);
        if (eventDay >= today) continue;
      }
      add(ev.title, null, ev.actionUrl || '/timeline');
    }
    return rows;
  }, [auditData, timelineEvents]);

  const upcomingDeadlines = useMemo(() => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);

    const fromTimeline = (timelineEvents || []).filter((ev) => {
      if (!ev.date || isActionOnlyTimelineEvent(ev)) return false;
      const eventDay = parseTimelineDateOnly(ev.date);
      if (!eventDay) return false;
      eventDay.setHours(0, 0, 0, 0);
      return eventDay >= today;
    });

    const fromAuditDueSoon = (auditData?.dueSoonItems || []).map((d, i) => ({
      id: `audit-due-${i}`,
      title: d.item,
      date: null,
      detail: d.detail || '',
      actionUrl: '/audit',
    }));

    const combined = [...fromTimeline, ...fromAuditDueSoon].sort((a, b) => {
      const da = a.date ? parseTimelineDateOnly(a.date)?.getTime() ?? Number.MAX_SAFE_INTEGER : Number.MAX_SAFE_INTEGER;
      const db = b.date ? parseTimelineDateOnly(b.date)?.getTime() ?? Number.MAX_SAFE_INTEGER : Number.MAX_SAFE_INTEGER;
      if (da !== db) return da - db;
      return String(a.title || '').localeCompare(String(b.title || ''));
    });

    return combined.slice(0, 12);
  }, [timelineEvents, auditData]);

  const formatDate = (d) =>
    d ? new Date(d).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : '';

  const docPercent = auditData?.readinessScore ?? 0;
  const nextMissingDoc = firstMissingDocLabel(auditData?.missingItems);
  const vehicleAvg = scoresData?.vehicles?.average != null ? Math.round(scoresData.vehicles.average) : 0;
  const vehicleIssue = vehicleIssueFromAudit(auditData?.missingItems) || 'No outstanding vehicle issues';
  const authPillar = authorityPillarStatus(authorityData);

  if (isLoading) {
    return (
      <div className="dashboard-container dashboard-page">
        <div className="dashboard-loading">
          <BrandLoading message="Loading your dashboard..." />
        </div>
      </div>
    );
  }

  if (!user) return null;

  return (
    <div className="dashboard-container dashboard-page">
      {showUpgradeBanner && (
        <div className="upgrade-banner">
          <div className="upgrade-banner-content">
            <h3>
              You&apos;re on the free plan. Upgrade to Owner-Operator and unlock your full DRI Score™ breakdown — $20/mo
            </h3>
          </div>
          <button type="button" className="button" onClick={() => navigate('/pricing')}>
            Upgrade Now
          </button>
        </div>
      )}
      <IdentityVerificationBanner user={user} />

      <ProductTour steps={dashboardTour} run={showTour} onFinish={handleTourFinish} tourName="dashboard" />

      {showChatbotHint && (
        <div
          className="chatbot-hint-notification"
          role="dialog"
          aria-label="Need Help?"
          onAnimationEnd={(event) => {
            if (event.animationName === 'fadeOut') dismissChatbotHint();
          }}
        >
          <div className="chatbot-hint-content">
            <button type="button" className="close-hint" onClick={dismissChatbotHint} aria-label="Close">
              <X size={20} />
            </button>
            <h3>Need Help?</h3>
            <p>Click Ask Rynn in the bottom right to ask compliance questions.</p>
            <div className="hint-arrow">↘</div>
          </div>
        </div>
      )}

      {showSuccessMessage && (
        <div className="dashboard-success">
          <p>
            Subscription activated. Welcome to{' '}
            {user.subscriptionTier === 'fleet' ? 'Fleet' : user.subscriptionTier === 'pro' ? 'Pro' : 'Owner-Operator'}!
          </p>
        </div>
      )}

      {error && (
        <div className="dashboard-error">
          <p>{error}</p>
        </div>
      )}

      <div className="dashboard-header">
        <div>
          <h1>Hello, {user.firstName || 'User'}!</h1>
          <p className="dashboard-subtitle">Your compliance intelligence, at a glance.</p>
          <IdentityStatusBadge user={user} />
        </div>
        <Button variant="secondary" icon={RefreshCw} onClick={() => window.location.reload()}>
          Refresh
        </Button>
      </div>

      <DriScoreHero
        score={driScore}
        loading={dashLoading}
        attentionCount={attentionCount}
        variant={isProOrFleetDashboard ? 'fleet' : 'personal'}
        fleetMetrics={isProOrFleetDashboard ? fleetDriMetrics : null}
      />

      {!dashLoading && shouldShowUnverifiedDocsBanner(userDocuments) ? (
        <motion.div
          className="dashboard-dri-unverified-banner"
          role="status"
          initial={{ opacity: 0, y: 8 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ type: 'spring', stiffness: 90, damping: 22 }}
        >
          Some documents are unverified. Verified documents contribute full weight to your DRI Score™.
        </motion.div>
      ) : null}

      <section
        className="dashboard-passport-card"
        aria-label={isProOrFleetDashboard ? 'Fleet Compliance Passport' : 'Compliance Passport'}
      >
        <div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 8 }}>
            <CreditCard size={22} color="var(--amber)" aria-hidden />
            <h2 className="dashboard-passport-card__title">
              {isProOrFleetDashboard ? 'Fleet Compliance Passport™' : 'Compliance Passport™'}
            </h2>
          </div>
          <p className="dashboard-passport-card__sub">
            Share your verified compliance record with insurers, brokers, and shippers.
          </p>
          {passportMeta?.isExpired && (
            <p className="dashboard-passport-card__meta">
              Your previous passport has expired. Generate a new one to share.
            </p>
          )}
          {passportMeta && !passportMeta.isExpired && passportMeta.passportId ?
            <>
              <p className="dashboard-passport-card__meta">
                Active · Expires{' '}
                {passportMeta.expiresAt ?
                  new Date(passportMeta.expiresAt).toLocaleDateString('en-US', {
                    month: 'short',
                    day: 'numeric',
                    year: 'numeric',
                  })
                : '—'}
              </p>
              <p className="dashboard-passport-card__meta">
                Viewed {passportMeta.viewCount ?? 0} time
                {(passportMeta.viewCount ?? 0) === 1 ? '' : 's'}
              </p>
              <div className="dashboard-passport-card__url-row">
                <span className="dashboard-passport-card__url" title={passportShareUrl}>
                  {passportShareUrl}
                </span>
                <button
                  type="button"
                  className="dashboard-passport-card__btn dashboard-passport-card__btn--secondary"
                  onClick={copyPassportLink}
                >
                  {passportCopyLabel}
                </button>
              </div>
              <div className="dashboard-passport-card__actions">
                <button
                  type="button"
                  className="dashboard-passport-card__btn"
                  onClick={() =>
                    isProOrFleetDashboard ?
                      navigate('/passport')
                    : window.open(
                        `/passport/${encodeURIComponent(passportMeta.passportId)}`,
                        '_blank',
                        'noopener,noreferrer',
                      )
                  }
                >
                  View Passport
                </button>
                <button
                  type="button"
                  className="dashboard-passport-card__btn dashboard-passport-card__btn--secondary"
                  onClick={handleRegeneratePassport}
                  disabled={passportBusy}
                >
                  Regenerate
                </button>
              </div>
            </>
          : !passportMeta || passportMeta.isExpired ?
            <div className="dashboard-passport-card__actions">
              <button
                type="button"
                className="dashboard-passport-card__btn"
                onClick={handleGeneratePassport}
                disabled={passportBusy}
              >
                {passportBusy ? 'Working…' : 'Generate My Passport™'}
              </button>
            </div>
          : null}
        </div>
        <div className="dashboard-passport-card__preview">
          <div className="dashboard-passport-card__preview-score">
            {dashLoading || driScore == null ? '—' : driScore}
            <span className="dashboard-passport-card__preview-max">/100</span>
          </div>
          {(() => {
            const pill = driPillStyle(driScore);
            return (
              <span className="dashboard-passport-card__preview-pill" style={{ backgroundColor: pill.color }}>
                {pill.label}
              </span>
            );
          })()}
        </div>
      </section>

      <section
        className={`dashboard-pillar-grid${isProOrFleetDashboard ? ' dashboard-pillar-grid--four' : ''}`}
        aria-label="Compliance pillars"
      >
        <div className="dashboard-pillar-card card" data-tour="dashboard-pillar-docs">
          <div className="dashboard-pillar-card__head">
            <FileCheck size={22} />
            <h3>Document Health</h3>
          </div>
          {dashLoading ? (
            <p className="dashboard-pillar-card__sub">Loading…</p>
          ) : (
            <>
              <div className="dashboard-pillar-card__metric">{docPercent}% complete</div>
              <p className="dashboard-pillar-card__sub">
                {nextMissingDoc ? <>Next missing: {nextMissingDoc}</> : <>All required documents on file.</>}
              </p>
            </>
          )}
          <button type="button" className="dashboard-pillar-card__cta" onClick={() => navigate('/documents')}>
            Upload Doc
          </button>
        </div>
        <div className="dashboard-pillar-card card" data-tour="dashboard-pillar-vehicles">
          <div className="dashboard-pillar-card__head">
            <Truck size={22} />
            <h3>Vehicle Readiness™</h3>
          </div>
          {dashLoading ? (
            <p className="dashboard-pillar-card__sub">Loading…</p>
          ) : (
            <>
              <div className="dashboard-pillar-card__metric">
                {vehicleAvg}
                <span className="dashboard-pillar-card__max">/100</span>
              </div>
              <p className="dashboard-pillar-card__sub dashboard-pillar-card__sub--clamp">{vehicleIssue}</p>
            </>
          )}
          <button type="button" className="dashboard-pillar-card__cta" onClick={() => navigate('/vehicles')}>
            Manage Vehicles
          </button>
        </div>
        {isProOrFleetDashboard ?
          <div className="dashboard-pillar-card card" aria-label="Driver compliance summary">
            <div className="dashboard-pillar-card__head">
              <Users size={22} />
              <h3>Driver Compliance</h3>
            </div>
            {driversLoading || dashLoading ?
              <p className="dashboard-pillar-card__sub">Loading…</p>
            : driverComplianceSummary && driverComplianceSummary.total === 0 ?
              <>
                <p className="dashboard-pillar-card__sub">No drivers added yet. Add your first driver.</p>
                <button type="button" className="dashboard-pillar-card__cta" onClick={() => navigate('/drivers')}>
                  Add driver
                </button>
              </>
            : driverComplianceSummary ?
              <>
                <div className="dashboard-pillar-card__metric">
                  {driverComplianceSummary.pass} of {driverComplianceSummary.total} drivers compliant
                </div>
                <div className="progress-bar" style={{ marginBottom: 'var(--space-sm)' }}>
                  <div className="progress-fill" style={{ width: `${driverComplianceSummary.pct}%` }} />
                </div>
                {driverComplianceSummary.failing > 0 ?
                  <p
                    className={`dashboard-pillar-card__sub dashboard-pillar-card__driver-attention${driverComplianceSummary.failing >= 2 ? ' dashboard-pillar-card__driver-attention--strong' : ''}`}
                  >
                    {driverComplianceSummary.failing} driver{driverComplianceSummary.failing !== 1 ? 's' : ''} need
                    attention
                  </p>
                : null}
                <button type="button" className="dashboard-pillar-card__cta" onClick={() => navigate('/drivers')}>
                  Manage Drivers →
                </button>
              </>
            : (
              <p className="dashboard-pillar-card__sub">Loading…</p>
            )}
          </div>
        : null}
        <div className="dashboard-pillar-card card" data-tour="dashboard-pillar-authority">
          <div className="dashboard-pillar-card__head">
            <ShieldCheck size={22} />
            <h3>Authority Health</h3>
          </div>
          {dashLoading ? (
            <p className="dashboard-pillar-card__sub">Loading…</p>
          ) : (
            <>
              <div className="dashboard-pillar-card__metric dashboard-pillar-card__metric--status">{authPillar.headline}</div>
              <p className="dashboard-pillar-card__sub dashboard-pillar-card__sub--clamp">{authPillar.dotLine}</p>
            </>
          )}
          <button type="button" className="dashboard-pillar-card__cta" onClick={() => navigate('/authority')}>
            View Authority
          </button>
        </div>
      </section>

      <section
        className="dashboard-quick-actions"
        aria-label="Quick Actions"
        data-tour="dashboard-quick-actions"
      >
        <Button variant="secondary" icon={FileText} onClick={() => navigate('/documents')}>
          Upload Doc
        </Button>
        <Button variant="secondary" icon={Truck} onClick={() => navigate('/vehicles')}>
          Add Vehicle
        </Button>
        <Button variant="secondary" icon={Shield} onClick={() => navigate('/documents')}>
          Update Insurance
        </Button>
        <Button variant="secondary" icon={AlertTriangle} onClick={() => navigate('/clearinghouse')}>
          Run Clearinghouse
        </Button>
      </section>

      <div className="dashboard-action-deadlines" aria-label="Action items and deadlines">
        <div className="dashboard-dense-section">
          <h3>Action Items</h3>
          <ul className="dashboard-dense-list">
            {actionItems.length === 0 ? (
              <li>
                <span>No items need fixing</span>
              </li>
            ) : (
              actionItems.map((row) => (
                <li key={row.id}>
                  <span>
                    {formatAnalyticsDocumentDisplay(row.title)}
                    {row.detail ? ` — ${formatAnalyticsDocumentDisplay(row.detail)}` : ''}
                  </span>
                  <a
                    href={row.fixUrl}
                    onClick={(e) => {
                      e.preventDefault();
                      navigate(row.fixUrl);
                    }}
                  >
                    Fix
                  </a>
                </li>
              ))
            )}
          </ul>
          <Button variant="ghost" size="sm" onClick={() => navigate('/timeline')} style={{ marginTop: 8 }}>
            View full timeline
          </Button>
        </div>
        <div className="dashboard-dense-section">
          <h3>Upcoming Deadlines</h3>
          <ul className="dashboard-dense-list">
            {upcomingDeadlines.length === 0 ? (
              <li>
                <span>No upcoming deadlines</span>
              </li>
            ) : (
              upcomingDeadlines.map((ev) => (
                <li key={ev.id}>
                  <span>{formatAnalyticsDocumentDisplay(ev.title)}</span>
                  <span className="text-small text-muted">
                    {ev.date ? formatDate(ev.date) : ev.detail || '—'}
                  </span>
                </li>
              ))
            )}
          </ul>
          <Button variant="ghost" size="sm" onClick={() => navigate('/timeline')} style={{ marginTop: 8 }}>
            View all
          </Button>
        </div>
      </div>

      <section className="dashboard-timeline-section" aria-label="Compliance timeline">
        <ComplianceTimelineWidget />
      </section>
    </div>
  );
};

export default Dashboard;
